import React, { useEffect, useState } from "react";

import { db } from "../firebase";

import {
  collection,
  onSnapshot,
  getDocs,
  updateDoc,
  doc,
  query,
  where,
  getDoc,
  writeBatch,
  serverTimestamp,
} from "firebase/firestore";

import {
  FaBell,
  FaExternalLinkAlt,
} from "react-icons/fa";

import { useNavigate } from "react-router-dom";

import "./Adminfutagroup.css";


function AdminTransactions() {

  // =====================================================
  // NAVIGATION
  // =====================================================

  const navigate = useNavigate();


  // =====================================================
  // STATE
  // =====================================================

  const [transactions, setTransactions] = useState([]);

  const [loading, setLoading] = useState(true);

  const [updatingId, setUpdatingId] = useState(null);

  const [approvedCount, setApprovedCount] = useState(0);

  const [approvedCountPerUser, setApprovedCountPerUser] =
    useState({});

  const [todayAmounts, setTodayAmounts] = useState({});

  const [todayReferrers, setTodayReferrers] = useState({});

  const [searchUser, setSearchUser] = useState("");

  // Transaction editing (pending/rejected only; successful transactions
  // require a separate reconciliation process).
  const [editingTx, setEditingTx] = useState(null);
  const [editTxid, setEditTxid] = useState("");
  const [editAmount, setEditAmount] = useState("");
  const [editReason, setEditReason] = useState("");
  const [savingEdit, setSavingEdit] = useState(false);


  // =====================================================
  // VISITOR DEPOSIT PROCESSING
  // =====================================================

  const [
    visitorProcessingDeposits,
    setVisitorProcessingDeposits,
  ] = useState([]);


  // =====================================================
  // USDT PROCESSING
  // =====================================================

  const [
    usdtProcessingTransactions,
    setUsdtProcessingTransactions,
  ] = useState([]);


  // =====================================================
  // 🔥 VOLATILITY FOREX PROCESSING
  //
  // Collections:
  // volatilityforex
  // volatilityotherbalance
  // =====================================================

  const [
    volatilityProcessingTransactions,
    setVolatilityProcessingTransactions,
  ] = useState([]);


  // =====================================================
  // REALTIME TRANSACTIONS
  // =====================================================

  useEffect(() => {

    let tshTransactions = [];

    let usdtTransactions = [];

    let tshLoaded = false;

    let usdtLoaded = false;


    // ===================================================
    // PROCESS ALL TRANSACTIONS
    // ===================================================

    const processTransactions = async () => {

      if (!tshLoaded || !usdtLoaded) {
        return;
      }


      try {

        // ===============================================
        // COMBINE TSH + USDT
        // ===============================================

        const combined = [

          ...tshTransactions.map((tx) => ({
            ...tx,
            collection: "tsh_transactions",
          })),

          ...usdtTransactions.map((tx) => ({
            ...tx,
            collection: "usdt_transactions",
          })),

        ];


        // ===============================================
        // SORT NEWEST FIRST
        // ===============================================

        combined.sort((a, b) => {

          const aTime =
            a.createdAt?.seconds ||
            a.timestamp?.seconds ||
            0;

          const bTime =
            b.createdAt?.seconds ||
            b.timestamp?.seconds ||
            0;

          return bTime - aTime;

        });


        // ===============================================
        // SET TRANSACTIONS
        // ===============================================

        setTransactions(combined);


        // ===============================================
        // APPROVED COUNT
        // ===============================================

        const totalApproved =
          combined.filter(
            (tx) => tx.status === "success"
          ).length;


        setApprovedCount(
          totalApproved
        );


        // ===============================================
        // APPROVED PER USER
        // ===============================================

        const countPerUser =
          combined.reduce((acc, tx) => {

            if (
              tx.status === "success"
            ) {

              const username =
                tx.username ||
                "Unknown";


              acc[username] =
                (acc[username] || 0) + 1;

            }


            return acc;

          }, {});


        setApprovedCountPerUser(
          countPerUser
        );


        // ===============================================
        // TODAY
        // ===============================================

        const today =
          new Date();

        today.setHours(
          0,
          0,
          0,
          0
        );


        const todayOnly =
          combined.filter((tx) => {

            const txDate =
              tx.createdAt?.seconds
                ? new Date(
                    tx.createdAt.seconds * 1000
                  )
                : tx.timestamp?.seconds
                ? new Date(
                    tx.timestamp.seconds * 1000
                  )
                : null;


            return (
              txDate &&
              txDate >= today &&
              tx.status === "success"
            );

          });


        // ===============================================
        // TODAY AMOUNT PER USER
        // ===============================================

        const totalPerUserToday =
          todayOnly.reduce((acc, tx) => {

            const username =
              tx.username ||
              "Unknown";


            const amount =
              Number(
                tx.amount || 0
              );


            acc[username] =
              (acc[username] || 0) +
              amount;


            return acc;

          }, {});


        setTodayAmounts(
          totalPerUserToday
        );


        // ===============================================
        // GET REFERRERS
        // ===============================================

        const usernamesToday =
          Object.keys(
            totalPerUserToday
          );


        const batchSize = 10;

        const referrersMap = {};


        // ===============================================
        // FETCH USERS
        // ===============================================

        for (
          let i = 0;
          i < usernamesToday.length;
          i += batchSize
        ) {

          const batch =
            usernamesToday.slice(
              i,
              i + batchSize
            );


          if (
            batch.length === 0
          ) {
            continue;
          }


          const usersQuery =
            query(
              collection(
                db,
                "users"
              ),
              where(
                "username",
                "in",
                batch
              )
            );


          const usersSnap =
            await getDocs(
              usersQuery
            );


          usersSnap.docs.forEach(
            (userDoc) => {

              const data =
                userDoc.data();


              referrersMap[
                data.username
              ] =
                data.referralBy ||
                null;

            }
          );


          batch.forEach(
            (username) => {

              if (
                referrersMap[
                  username
                ] === undefined
              ) {

                referrersMap[
                  username
                ] = null;

              }

            }
          );

        }


        // ===============================================
        // UNIQUE REFERRERS
        // ===============================================

        const referralValues = [
          ...new Set(
            Object.values(
              referrersMap
            ).filter(
              (value) =>
                value !== null &&
                value !== ""
            )
          ),
        ];


        // ===============================================
        // RESOLVE USERNAME
        // ===============================================

        const resolvedByUsername = {};

        const unresolvedValues =
          new Set(
            referralValues
          );


        for (
          let i = 0;
          i < referralValues.length;
          i += batchSize
        ) {

          const batch =
            referralValues.slice(
              i,
              i + batchSize
            );


          if (
            batch.length === 0
          ) {
            continue;
          }


          const usersQuery =
            query(
              collection(
                db,
                "users"
              ),
              where(
                "username",
                "in",
                batch
              )
            );


          const usersSnap =
            await getDocs(
              usersQuery
            );


          usersSnap.docs.forEach(
            (userDoc) => {

              const data =
                userDoc.data();


              if (
                data.username
              ) {

                resolvedByUsername[
                  data.username
                ] =
                  data.username;


                unresolvedValues.delete(
                  data.username
                );

              }

            }
          );

        }


        // ===============================================
        // RESOLVE REFERRAL CODE
        // ===============================================

        const codeToUsername = {};

        const unresolvedList =
          Array.from(
            unresolvedValues
          );


        for (
          let i = 0;
          i < unresolvedList.length;
          i += batchSize
        ) {

          const batch =
            unresolvedList.slice(
              i,
              i + batchSize
            );


          if (
            batch.length === 0
          ) {
            continue;
          }


          const codeQuery =
            query(
              collection(
                db,
                "users"
              ),
              where(
                "referralCode",
                "in",
                batch
              )
            );


          const codeSnap =
            await getDocs(
              codeQuery
            );


          codeSnap.docs.forEach(
            (userDoc) => {

              const data =
                userDoc.data();


              if (
                data.referralCode &&
                data.username
              ) {

                codeToUsername[
                  data.referralCode
                ] =
                  data.username;

              }

            }
          );

        }


        // ===============================================
        // FINAL REFERRERS
        // ===============================================

        const finalReferrers = {};


        Object.entries(
          referrersMap
        ).forEach(
          ([username, refVal]) => {

            if (!refVal) {

              finalReferrers[
                username
              ] =
                "Hakuna";

              return;

            }


            if (
              resolvedByUsername[
                refVal
              ]
            ) {

              finalReferrers[
                username
              ] =
                resolvedByUsername[
                  refVal
                ];

              return;

            }


            if (
              codeToUsername[
                refVal
              ]
            ) {

              finalReferrers[
                username
              ] =
                codeToUsername[
                  refVal
                ];

              return;

            }


            finalReferrers[
              username
            ] =
              "Haijulikani";

          }
        );


        setTodayReferrers(
          finalReferrers
        );


        setLoading(false);

      }

      catch (error) {

        console.error(
          "Error processing transactions:",
          error
        );

        setLoading(false);

      }

    };


    // ===================================================
    // TSH REALTIME
    // ===================================================

    const unsubscribeTsh =
      onSnapshot(
        collection(
          db,
          "tsh_transactions"
        ),

        (snapshot) => {

          tshTransactions =
            snapshot.docs.map(
              (document) => ({

                id:
                  document.id,

                ...document.data(),

              })
            );


          tshLoaded = true;

          processTransactions();

        },

        (error) => {

          console.error(
            "TSH realtime error:",
            error
          );


          tshLoaded = true;

          processTransactions();

        }
      );


    // ===================================================
    // USDT REALTIME
    // ===================================================

    const unsubscribeUsdt =
      onSnapshot(
        collection(
          db,
          "usdt_transactions"
        ),

        (snapshot) => {

          usdtTransactions =
            snapshot.docs.map(
              (document) => ({

                id:
                  document.id,

                ...document.data(),

              })
            );


          // =============================================
          // FIND PROCESSING USDT
          // =============================================

          const processing =
            snapshot.docs
              .map(
                (document) => ({

                  id:
                    document.id,

                  ...document.data(),

                })
              )
              .filter(
                (tx) =>
                  String(
                    tx.status || ""
                  ).toLowerCase() ===
                    "processing" ||

                  String(
                    tx.status || ""
                  ).toLowerCase() ===
                    "pending"
              )
              .sort(
                (a, b) => {

                  const aTime =
                    a.createdAt?.seconds ||
                    a.timestamp?.seconds ||
                    0;

                  const bTime =
                    b.createdAt?.seconds ||
                    b.timestamp?.seconds ||
                    0;

                  return bTime - aTime;

                }
              );


          setUsdtProcessingTransactions(
            processing
          );


          usdtLoaded = true;

          processTransactions();

        },

        (error) => {

          console.error(
            "USDT realtime error:",
            error
          );


          usdtLoaded = true;

          processTransactions();

        }
      );


    // ===================================================
    // CLEANUP
    // ===================================================

    return () => {

      unsubscribeTsh();

      unsubscribeUsdt();

    };

  }, []);


  // =====================================================
  // VISITOR DEPOSIT REALTIME
  // =====================================================

  useEffect(() => {

    const unsubscribeVisitor =
      onSnapshot(
        collection(
          db,
          "visterdeposte"
        ),

        (snapshot) => {

          const processing =
            snapshot.docs
              .map(
                (document) => ({

                  id:
                    document.id,

                  ...document.data(),

                })
              )
              .filter(
                (deposit) =>
                  String(
                    deposit.status || ""
                  ).toLowerCase() ===
                  "processing"
              )
              .sort(
                (a, b) => {

                  const aTime =
                    a.createdAt?.seconds ||
                    a.timestamp?.seconds ||
                    0;

                  const bTime =
                    b.createdAt?.seconds ||
                    b.timestamp?.seconds ||
                    0;

                  return bTime - aTime;

                }
              );


          setVisitorProcessingDeposits(
            processing
          );

        },

        (error) => {

          console.error(
            "Visitor deposit realtime error:",
            error
          );

        }
      );


    return () => {

      unsubscribeVisitor();

    };

  }, []);


  // =====================================================
  // 🔥 VOLATILITY FOREX + OTHER BALANCE REALTIME
  //
  // volatilityforex
  // volatilityotherbalance
  //
  // STATUS:
  // processing
  // =====================================================

  useEffect(() => {

    let forexTransactions = [];

    let otherBalanceTransactions = [];


    const updateVolatilityProcessing = () => {

      const forexProcessing =
        forexTransactions
          .filter(
            (tx) =>
              String(
                tx.status || ""
              ).toLowerCase() ===
              "processing"
          )
          .map(
            (tx) => ({
              ...tx,
              sourceCollection:
                "volatilityforex",
            })
          );


      const otherBalanceProcessing =
        otherBalanceTransactions
          .filter(
            (tx) =>
              String(
                tx.status || ""
              ).toLowerCase() ===
              "processing"
          )
          .map(
            (tx) => ({
              ...tx,
              sourceCollection:
                "volatilityotherbalance",
            })
          );


      const combined = [
        ...forexProcessing,
        ...otherBalanceProcessing,
      ];


      // ===============================================
      // NEWEST FIRST
      // ===============================================

      combined.sort(
        (a, b) => {

          const aTime =
            a.createdAt?.seconds ||
            a.timestamp?.seconds ||
            a.updatedAt?.seconds ||
            0;

          const bTime =
            b.createdAt?.seconds ||
            b.timestamp?.seconds ||
            b.updatedAt?.seconds ||
            0;

          return bTime - aTime;

        }
      );


      setVolatilityProcessingTransactions(
        combined
      );

    };


    // ===================================================
    // VOLATILITYFOREX
    // ===================================================

    const unsubscribeForex =
      onSnapshot(
        collection(
          db,
          "volatilityforex"
        ),

        (snapshot) => {

          forexTransactions =
            snapshot.docs.map(
              (document) => ({

                id:
                  document.id,

                ...document.data(),

              })
            );


          updateVolatilityProcessing();

        },

        (error) => {

          console.error(
            "VolatilityForex realtime error:",
            error
          );


          forexTransactions = [];

          updateVolatilityProcessing();

        }
      );


    // ===================================================
    // VOLATILITYOTHERBALANCE
    // ===================================================

    const unsubscribeOtherBalance =
      onSnapshot(
        collection(
          db,
          "volatilityotherbalance"
        ),

        (snapshot) => {

          otherBalanceTransactions =
            snapshot.docs.map(
              (document) => ({

                id:
                  document.id,

                ...document.data(),

              })
            );


          updateVolatilityProcessing();

        },

        (error) => {

          console.error(
            "VolatilityOtherBalance realtime error:",
            error
          );


          otherBalanceTransactions = [];

          updateVolatilityProcessing();

        }
      );


    // ===================================================
    // CLEANUP
    // ===================================================

    return () => {

      unsubscribeForex();

      unsubscribeOtherBalance();

    };

  }, []);


  // =====================================================
  // OPEN VISITOR DEPOSITS
  // =====================================================

  const openVisitorDeposits = () => {

    navigate(
      "/Admin-VistorDeposit"
    );

  };


  // =====================================================
  // OPEN USDT TRANSACTIONS
  // =====================================================

  const openUsdtTransactions = () => {

    navigate(
      "/Admin-Usdt-Transaction"
    );

  };


  // =====================================================
  // 🔥 OPEN VOLATILITY FOREX TRANSACTIONS
  // =====================================================

  const openVolatilityTransactions = () => {

    navigate(
      "/AdminVolatility-ForexTransaction"
    );

  };


  // =====================================================
  // UPDATE TRANSACTION STATUS
  // =====================================================

  const updateStatus = async (
    id,
    collectionName,
    newStatus
  ) => {

    setUpdatingId(id);


    try {

      const tx =
        transactions.find(
          (t) =>
            t.id === id &&
            t.collection === collectionName
        );


      if (!tx) {
        return;
      }


      // ===============================================
      // PREVENT DUPLICATE SUCCESS PROCESSING
      // ===============================================

      if (
        tx.status === "success" &&
        newStatus === "success"
      ) {

        return;

      }


      const docRef =
        doc(
          db,
          collectionName,
          id
        );


      let updateData = {
        status:
          newStatus,
      };


      // ===============================================
      // POSITION
      // ===============================================

      if (
        tx.status !== "success" &&
        newStatus === "success"
      ) {

        const userTxs =
          transactions
            .filter(
              (t) =>
                t.username ===
                  tx.username &&
                t.status ===
                  "success" &&
                !(
                  t.id === id &&
                  t.collection ===
                    collectionName
                )
            )
            .sort(
              (a, b) => {

                const aTime =
                  a.createdAt?.seconds ||
                  a.timestamp?.seconds ||
                  0;

                const bTime =
                  b.createdAt?.seconds ||
                  b.timestamp?.seconds ||
                  0;

                return aTime - bTime;

              }
            );


        if (
          userTxs.length === 0
        ) {

          updateData.position =
            "first";

        }

        else if (
          userTxs.length === 1
        ) {

          updateData.position =
            "second";

        }

        else {

          updateData.position =
            "other";

        }

      }

      else {

        updateData.position =
          tx.position ||
          "other";

      }


      // ===============================================
      // UPDATE TRANSACTION
      // ===============================================

      await updateDoc(
        docRef,
        updateData
      );


      // ===============================================
      // LOCAL UPDATE
      // ===============================================

      setTransactions(
        (prev) =>
          prev.map(
            (t) => {

              if (
                t.id === id &&
                t.collection ===
                  collectionName
              ) {

                return {
                  ...t,
                  ...updateData,
                };

              }


              return t;

            }
          )
      );


      // ===============================================
      // APPROVED COUNTERS
      // ===============================================

      setApprovedCount(
        (prevCount) => {

          let newTotal =
            prevCount;


          const username =
            tx.username ||
            "Unknown";


          setApprovedCountPerUser(
            (prevUserCounts) => {

              const newUserCounts = {
                ...prevUserCounts,
              };


              if (
                tx.status !==
                  "success" &&
                newStatus ===
                  "success"
              ) {

                newTotal =
                  prevCount + 1;


                newUserCounts[
                  username
                ] =
                  (
                    newUserCounts[
                      username
                    ] || 0
                  ) + 1;

              }

              else if (
                tx.status ===
                  "success" &&
                newStatus !==
                  "success"
              ) {

                newTotal =
                  prevCount - 1;


                newUserCounts[
                  username
                ] =
                  (
                    newUserCounts[
                      username
                    ] || 1
                  ) - 1;


                if (
                  newUserCounts[
                    username
                  ] <= 0
                ) {

                  delete newUserCounts[
                    username
                  ];

                }

              }


              return newUserCounts;

            }
          );


          return newTotal;

        }
      );


      // ===============================================
      // UPDATE USER BALANCE
      // ===============================================

      if (
        tx.status !==
          "success" &&
        newStatus ===
          "success"
      ) {

        const username =
          tx.username;


        const amount =
          Number(
            tx.amount || 0
          );


        // =============================================
        // USDT → usdtBalance
        // TSH → tshBalance
        // =============================================

        const balanceField =
          collectionName ===
          "usdt_transactions"
            ? "usdtBalance"
            : "tshBalance";


        if (!username) {

          console.error(
            "Username missing. Balance cannot be updated."
          );

          return;

        }


        if (
          !Number.isFinite(
            amount
          )
        ) {

          console.error(
            "Invalid transaction amount:",
            tx.amount
          );

          return;

        }


        const usersRef =
          collection(
            db,
            "users"
          );


        const userQuery =
          query(
            usersRef,
            where(
              "username",
              "==",
              username
            )
          );


        const userSnap =
          await getDocs(
            userQuery
          );


        if (
          userSnap.empty
        ) {

          console.error(
            `User not found: ${username}`
          );

          return;

        }


        const userDoc =
          userSnap.docs[0];


        const userRef =
          doc(
            db,
            "users",
            userDoc.id
          );


        const currentBalance =
          Number(
            userDoc.data()[
              balanceField
            ] || 0
          );


        const newBalance =
          currentBalance +
          amount;


        await updateDoc(
          userRef,
          {
            [balanceField]:
              newBalance,
          }
        );


        console.log(
          `✅ ${username} ${balanceField} updated from ${currentBalance} to ${newBalance}`
        );

      }

    }

    catch (error) {

      console.error(
        "Error updating status:",
        error
      );

      alert(
        "Imeshindikana kubadilisha status."
      );

    }

    finally {

      setUpdatingId(
        null
      );

    }

  };


  // =====================================================
  // EDIT TRANSACTION TXID + AMOUNT
  // Successful transactions are deliberately blocked here so a
  // financial record cannot be changed without reconciliation.
  // =====================================================

  const startEditTransaction = (tx) => {
    if (tx.status === "success") {
      alert(
        "Muamala huu umekamilika. Tumia utaratibu wa reconciliation ili kurekebisha rekodi na balance kwa usahihi."
      );
      return;
    }

    setEditingTx(tx);
    setEditTxid(String(tx.transactionId || tx.txId || ""));
    setEditAmount(String(tx.amount ?? ""));
    setEditReason("");
  };

  const saveTransactionEdit = async () => {
    if (!editingTx || savingEdit) return;

    const amountText = editAmount.trim();
    const amount = Number(amountText);
    const newTxid = editTxid.trim();
    const reason = editReason.trim();

    if (!amountText || !Number.isFinite(amount) || amount <= 0) {
      alert("Weka amount sahihi inayozidi sifuri.");
      return;
    }

    if (!newTxid) {
      alert("TXID haiwezi kuwa tupu.");
      return;
    }

    if (!reason) {
      alert("Eleza sababu ya marekebisho.");
      return;
    }

    setSavingEdit(true);

    try {
      const tx = editingTx;
      const txRef = doc(db, tx.collection, tx.id);
      const latestSnap = await getDoc(txRef);

      if (!latestSnap.exists()) {
        throw new Error("Transaction haipo tena kwenye database.");
      }

      const latest = latestSnap.data();
      if (String(latest.status || "").toLowerCase() === "success") {
        throw new Error(
          "Transaction imekuwa success. Edit imezuiwa; tumia reconciliation."
        );
      }

      const oldTxid = String(latest.transactionId || latest.txId || "");
      const oldAmount = Number(latest.amount ?? 0);
      const batch = writeBatch(db);
      const updateData = {
        transactionId: newTxid,
        amount,
        updatedAt: serverTimestamp(),
      };

      // Keep txId in sync when the document already uses that field.
      if (Object.prototype.hasOwnProperty.call(latest, "txId")) {
        updateData.txId = newTxid;
      }

      batch.update(txRef, updateData);

      // Audit record: preserves before/after values and the reason.
      const auditRef = doc(collection(db, "transaction_edits"));
      batch.set(auditRef, {
        transactionDocumentId: tx.id,
        sourceCollection: tx.collection,
        username: latest.username || latest.fullName || "",
        oldTxid,
        newTxid,
        oldAmount,
        newAmount: amount,
        reason,
        editedAt: serverTimestamp(),
      });

      await batch.commit();

      setTransactions((prev) =>
        prev.map((item) =>
          item.id === tx.id && item.collection === tx.collection
            ? { ...item, ...updateData }
            : item
        )
      );

      setEditingTx(null);
      setEditTxid("");
      setEditAmount("");
      setEditReason("");
      alert("Marekebisho yamehifadhiwa.");
    } catch (error) {
      console.error("Transaction edit error:", error);
      alert(error.message || "Imeshindikana kuhifadhi marekebisho.");
    } finally {
      setSavingEdit(false);
    }
  };


  // =====================================================
  // SEARCH
  // =====================================================

  const filteredTransactions =
    transactions.filter(
      (tx) => {

        const user =
          (
            tx.username ||
            tx.fullName ||
            ""
          ).toLowerCase();


        return user.includes(
          searchUser.toLowerCase()
        );

      }
    );


  // =====================================================
  // VOLATILITY PROCESSING COUNT
  // =====================================================

  const volatilityProcessingCount =
    volatilityProcessingTransactions.length;


  // =====================================================
  // RENDER
  // =====================================================

  return (

    <div className="admin-transactions-container">


      {/* =================================================
          HEADER
      ================================================= */}

      <h2>
        Admin - Miamala
      </h2>


      {/* =================================================
          🔥 VOLATILITY FOREX / OTHER BALANCE
          PROCESSING NOTIFICATION
      ================================================= */}

      {volatilityProcessingCount > 0 && (

        <button
          type="button"
          onClick={
            openVolatilityTransactions
          }
          style={{
            width: "100%",
            marginBottom: "20px",
            padding: "15px 18px",
            border: "none",
            borderRadius: "12px",
            background:
              "linear-gradient(135deg, #7c4dff, #536dfe)",
            color: "#fff",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: "15px",
            cursor: "pointer",
            boxShadow:
              "0 8px 25px rgba(83, 109, 254, 0.30)",
            textAlign: "left",
          }}
        >

          {/* ============================================
              LEFT
          ============================================ */}

          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "12px",
              minWidth: 0,
            }}
          >

            <div
              style={{
                width: "42px",
                height: "42px",
                flexShrink: 0,
                borderRadius: "50%",
                background:
                  "rgba(0,0,0,0.20)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: "19px",
              }}
            >

              <FaBell />

            </div>


            <div>

              <div
                style={{
                  fontSize: "16px",
                  fontWeight: "800",
                  marginBottom: "3px",
                }}
              >

                Volatility Forex

              </div>


              <div
                style={{
                  fontSize: "13px",
                  fontWeight: "600",
                }}
              >

                Kuna{" "}

                <strong>
                  {
                    volatilityProcessingCount
                  }
                </strong>{" "}

                deposit
                {
                  volatilityProcessingCount >
                  1
                    ? "s"
                    : ""
                }{" "}

                zinazosubiri kushughulikiwa

              </div>

            </div>

          </div>


          {/* ============================================
              RIGHT
          ============================================ */}

          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
              flexShrink: 0,
              fontSize: "13px",
              fontWeight: "800",
            }}
          >

            Processing

            <FaExternalLinkAlt />

          </div>

        </button>

      )}


      {/* =================================================
          🔥 USDT PROCESSING ALERT
      ================================================= */}

      {usdtProcessingTransactions.length > 0 && (

        <button
          type="button"
          onClick={
            openUsdtTransactions
          }
          style={{
            width: "100%",
            marginBottom: "20px",
            padding: "15px 18px",
            border: "none",
            borderRadius: "12px",
            background:
              "linear-gradient(135deg, #ff9800, #ff6d00)",
            color: "#fff",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: "15px",
            cursor: "pointer",
            boxShadow:
              "0 8px 25px rgba(255, 152, 0, 0.25)",
            textAlign: "left",
          }}
        >

          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "12px",
              minWidth: 0,
            }}
          >

            <div
              style={{
                width: "42px",
                height: "42px",
                flexShrink: 0,
                borderRadius: "50%",
                background:
                  "rgba(0,0,0,0.20)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: "19px",
              }}
            >

              <FaBell />

            </div>


            <div>

              <div
                style={{
                  fontSize: "16px",
                  fontWeight: "800",
                  marginBottom: "3px",
                }}
              >

                USDT Transaction

              </div>


              <div
                style={{
                  fontSize: "13px",
                  fontWeight: "600",
                }}
              >

                Kuna{" "}

                <strong>
                  {
                    usdtProcessingTransactions.length
                  }
                </strong>{" "}

                USDT transaction
                {
                  usdtProcessingTransactions.length >
                  1
                    ? "s"
                    : ""
                }{" "}

                zinazosubiri kushughulikiwa

              </div>

            </div>

          </div>


          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
              flexShrink: 0,
              fontSize: "13px",
              fontWeight: "800",
            }}
          >

            Processing

            <FaExternalLinkAlt />

          </div>

        </button>

      )}


      {/* =================================================
          🔥 VISITOR DEPOSIT PROCESSING ALERT
      ================================================= */}

      {visitorProcessingDeposits.length > 0 && (

        <button
          type="button"
          onClick={
            openVisitorDeposits
          }
          style={{
            width: "100%",
            marginBottom: "20px",
            padding: "15px 18px",
            border: "none",
            borderRadius: "12px",
            background:
              "linear-gradient(135deg, #00e676, #00c853)",
            color: "#06130a",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: "15px",
            cursor: "pointer",
            boxShadow:
              "0 8px 25px rgba(0, 230, 118, 0.25)",
            textAlign: "left",
          }}
        >

          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "12px",
              minWidth: 0,
            }}
          >

            <div
              style={{
                width: "42px",
                height: "42px",
                flexShrink: 0,
                borderRadius: "50%",
                background:
                  "rgba(0,0,0,0.18)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: "19px",
              }}
            >

              <FaBell />

            </div>


            <div>

              <div
                style={{
                  fontSize: "16px",
                  fontWeight: "800",
                  marginBottom: "3px",
                }}
              >

                Visitor Deposit

              </div>


              <div
                style={{
                  fontSize: "13px",
                  fontWeight: "600",
                }}
              >

                There are{" "}

                <strong>
                  {
                    visitorProcessingDeposits.length
                  }
                </strong>{" "}

                deposit
                {
                  visitorProcessingDeposits.length >
                  1
                    ? "s"
                    : ""
                }{" "}

                waiting to be processed

              </div>

            </div>

          </div>


          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
              flexShrink: 0,
              fontSize: "13px",
              fontWeight: "800",
            }}
          >

            Fungua

            <FaExternalLinkAlt />

          </div>

        </button>

      )}


      {/* =================================================
          LOADING
      ================================================= */}

      {loading ? (

        <p>
          Inapakia...
        </p>

      ) : (

        <div
          style={{
            display: "flex",
            gap: "20px",
            flexWrap: "wrap",
          }}
        >


          {/* =================================================
              TODAY
          ================================================= */}

          <div
            style={{
              flex: "1",
              background: "#111",
              padding: "15px",
              borderRadius: "10px",
              maxWidth: "350px",
              color: "#fff",
            }}
          >

            <h3
              style={{
                color: "#00ff99",
              }}
            >

              Miamala ya Leo

            </h3>


            {Object.keys(
              todayAmounts
            ).length === 0 ? (

              <p
                style={{
                  color: "#999",
                }}
              >

                Hakuna miamala ya leo bado.

              </p>

            ) : (

              <ul
                style={{
                  listStyle: "none",
                  padding: 0,
                }}
              >

                {Object.entries(
                  todayAmounts
                ).map(
                  ([
                    user,
                    amount,
                  ]) => (

                    <li
                      key={user}
                      style={{
                        borderBottom:
                          "1px solid #444",
                        padding: "8px 0",
                        color: "#eee",
                      }}
                    >

                      <strong>
                        {user}
                      </strong>{" "}


                      <span
                        style={{
                          fontSize:
                            "0.85em",
                          color:
                            "#aaa",
                        }}
                      >

                        (Aliletwa na:{" "}

                        {
                          todayReferrers[
                            user
                          ] ||
                          "Haijulikani"
                        }

                        )

                      </span>


                      <br />


                      {amount.toLocaleString()}

                    </li>

                  )
                )}

              </ul>

            )}

          </div>


          {/* =================================================
              MAIN
          ================================================= */}

          <div
            style={{
              flex: "3",
            }}
          >

            <p>

              Idadi ya Depositi
              Zilizokubaliwa:{" "}

              <strong>
                {approvedCount}
              </strong>

            </p>


            {/* =================================================
                APPROVED PER USER
            ================================================= */}

            <div
              style={{
                marginBottom:
                  "20px",
                background:
                  "#222",
                padding:
                  "10px",
                borderRadius:
                  "8px",
              }}
            >

              <h3
                style={{
                  color:
                    "#ffd700",
                }}
              >

                Depositi Zilizokubaliwa
                Kwa User

              </h3>


              <input
                type="text"
                placeholder="Tafuta user..."
                value={
                  searchUser
                }
                onChange={
                  (e) =>
                    setSearchUser(
                      e.target.value
                    )
                }
                style={{
                  width:
                    "100%",
                  padding:
                    "8px",
                  marginBottom:
                    "10px",
                  borderRadius:
                    "4px",
                  border:
                    "none",
                  fontSize:
                    "16px",
                }}
              />


              {Object.keys(
                approvedCountPerUser
              ).length === 0 ? (

                <p
                  style={{
                    color:
                      "#ffea00",
                  }}
                >

                  Hakuna deposits
                  zilizokubaliwa kwa
                  watumiaji bado.

                </p>

              ) : (

                <ul
                  style={{
                    listStyle:
                      "none",
                    paddingLeft:
                      0,
                    maxHeight:
                      "200px",
                    overflowY:
                      "auto",
                  }}
                >

                  {Object.entries(
                    approvedCountPerUser
                  )
                    .filter(
                      ([user]) =>
                        user
                          .toLowerCase()
                          .includes(
                            searchUser.toLowerCase()
                          )
                    )
                    .map(
                      ([
                        user,
                        count,
                      ]) => (

                        <li
                          key={user}
                          style={{
                            padding:
                              "5px 0",
                            borderBottom:
                              "1px solid #444",
                            color:
                              "#ffd700",
                          }}
                        >

                          <strong>
                            {user}
                          </strong>

                          :{" "}

                          {count}

                        </li>

                      )
                    )}

                </ul>

              )}

            </div>


            {/* =================================================
                TRANSACTIONS
            ================================================= */}

            {filteredTransactions.length === 0 ? (

              <p
                style={{
                  color:
                    "#ff6666",
                }}
              >

                Hakuna miamala kwa
                mtumiaji huyu.

              </p>

            ) : (

              <table className="admin-transactions-table">
                <thead>

                  <tr>

                    <th>
                      POSITION
                    </th>

                    <th>
                      TAREHE
                    </th>

                    <th>
                      USERNAME
                    </th>

                    <th>
                      METODA
                    </th>

                    <th>
                      USERNAMBA
                    </th>

                    <th>
                      BUSINESS NUMBER
                    </th>

                    <th>
                      KIASI
                    </th>

                    <th>
                      TXID
                    </th>

                    <th>
                      PAID TO
                    </th>

                    <th>
                      HALI
                    </th>

                    <th>
                      BADILISHA
                    </th>

                    <th>
                      EDIT TRANSACTION
                    </th>

                  </tr>

                </thead>


                <tbody>

                  {filteredTransactions.map(
                    (tx) => (

                      <tr
                        key={`${tx.collection}-${tx.id}`}
                      >

                        <td>

                          {
                            tx.position ||
                            "other"
                          }

                        </td>


                        <td>

                          {(
                            tx.createdAt?.seconds ||
                            tx.timestamp?.seconds
                          ) ? (

                            new Date(

                              (
                                tx.createdAt?.seconds ||
                                tx.timestamp?.seconds
                              ) * 1000

                            ).toLocaleString()

                          ) : (

                            "-"

                          )}

                        </td>


                        <td>

                          {
                            tx.username ||
                            tx.fullName ||
                            "N/A"
                          }

                        </td>


                        <td>

                          {
                            tx.provider ||
                            (
                              tx.collection ===
                              "tsh_transactions"

                                ? "TSH"

                                : "USDT"
                            )
                          }

                        </td>


                        <td>

                          {
                            tx.phone ||
                            tx.userPhoneNumber ||
                            tx.userUSDTAddress ||
                            "-"
                          }

                        </td>


                        <td>

                          {
                            tx.businessNumber ||
                            "-"
                          }

                        </td>


                        <td>

                          {Number(
                            tx.amount ||
                            0
                          ).toLocaleString()}

                        </td>


                        <td>

                          {
                            tx.transactionId ||
                            tx.txId ||
                            "Hakuna"
                          }

                        </td>


                        <td>

                          {
                            tx.recipient ||
                            tx.paidTo ||
                            "-"
                          }

                        </td>


                        <td>

                          <span
                            className={`status ${tx.status}`}
                          >

                            {
                              tx.status ===
                              "pending"

                                ? "Processing"

                                : tx.status ===
                                  "success"

                                ? "Success"

                                : tx.status ===
                                  "rejected"

                                ? "Rejected"

                                : tx.status
                            }

                          </span>

                        </td>


                        <td>

                          <select
                            disabled={
                              updatingId ===
                              tx.id
                            }
                            value={
                              tx.status
                            }
                            onChange={
                              (e) =>
                                updateStatus(
                                  tx.id,
                                  tx.collection,
                                  e.target.value
                                )
                            }
                          >

                            <option value="pending">
                              Processing
                            </option>

                            <option value="success">
                              Success
                            </option>

                            <option value="rejected">
                              Rejected
                            </option>

                          </select>

                        </td>

                        <td>
                          {editingTx?.id === tx.id &&
                          editingTx?.collection === tx.collection ? (
                            <div style={{ minWidth: "220px" }}>
                              <label>TXID</label>
                              <input
                                type="text"
                                value={editTxid}
                                onChange={(e) => setEditTxid(e.target.value)}
                                disabled={savingEdit}
                                style={{ width: "100%", marginBottom: "8px" }}
                              />

                              <label>Amount</label>
                              <input
                                type="number"
                                min="0.01"
                                step="any"
                                value={editAmount}
                                onChange={(e) => setEditAmount(e.target.value)}
                                disabled={savingEdit}
                                style={{ width: "100%", marginBottom: "8px" }}
                              />

                              <label>Sababu ya marekebisho</label>
                              <input
                                type="text"
                                value={editReason}
                                onChange={(e) => setEditReason(e.target.value)}
                                disabled={savingEdit}
                                placeholder="Mfano: kosa la kuingiza taarifa"
                                style={{ width: "100%", marginBottom: "8px" }}
                              />

                              <button
                                type="button"
                                disabled={savingEdit}
                                onClick={saveTransactionEdit}
                              >
                                {savingEdit ? "Inahifadhi..." : "Hifadhi"}
                              </button>

                              <button
                                type="button"
                                disabled={savingEdit}
                                onClick={() => {
                                  setEditingTx(null);
                                  setEditTxid("");
                                  setEditAmount("");
                                  setEditReason("");
                                }}
                                style={{ marginLeft: "6px" }}
                              >
                                Ghairi
                              </button>
                            </div>
                          ) : (
                            <button
                              type="button"
                              disabled={savingEdit}
                              onClick={() => startEditTransaction(tx)}
                            >
                              Edit Transaction
                            </button>
                          )}
                        </td>

                      </tr>

                    )
                  )}

                </tbody>

                </table>


            )}

          </div>

        </div>

      )}

    </div>

  );

}


export default AdminTransactions;